// drain-core.ts (Story 7.4) — the SINGLE, PURE queue-draining module, imported by the main
// thread (main.tsx, npm Dexie + apiFetch). One source of truth for sync logic: an evaluation
// created or edited offline replays under optimistic concurrency with no duplicates and no id
// reconciliation. (A second, Service-Worker consumer existed until seam-74 removed it — the
// main-thread drain is now the only replay path.)
//
// WHY this module imports NOTHING but types and PURE helpers from `idb-schema-def` (no
// `import Dexie`, no `api-client`, no `window`/`document`/`localStorage`, no React/TanStack Query):
// the Dexie DB instance and the network transport are passed in via dependency injection
// (`DrainDeps`). That keeps the module storage- and transport-agnostic (the split that forced 7.3 to
// extract the pure `idb-schema-def.ts`), and trivially unit-testable with a mocked transport +
// `fake-indexeddb`. `filterOwned` (Story 19.6 Unit E) is a pure predicate from that same pure
// module — which is exactly why it lives there rather than in the Dexie-side `owner-scope.ts`.
//
// The transport wiring (drain-runner, Task 4) owns the HTTP mapping: it routes a `create` op to
// `POST /evaluations/create` (payload incl. the client-minted id) and an `update` op to
// `POST /evaluations/{id}/save-draft` (+ `X-Client-Version`), then normalizes the response /
// `ApiError` into a `ReplayResult`. drain-core never knows about HTTP verbs or paths.

import { filterOwned } from "./idb-schema-def";
import type { ConflictBlock, DraftRecord, OutboundOp, ServerState } from "./idb-schema-def";

// `ServerState` lives alongside the schema (idb-schema-def) so `ConflictBlock` carries its typed
// shape with no cast; re-exported here for the per-context wirings that build `ReplayResult`s.
export type { ServerState } from "./idb-schema-def";

/**
 * The normalized outcome of replaying one op, produced by the per-context transport.
 *  - success: `200` (update save / idempotent-create replay) or `201` (first create) + the
 *    PHI-free response body (we only need `version`).
 *  - conflict: `409` (version_conflict) or `410` (already_submitted) + `server_state`.
 *  - non-retryable error: a terminal gate refusal (Story 8.1 — 403 `CALIB-001`
 *    calibration_incomplete) → the op is DEQUEUED, never retried; the local draft is
 *    left untouched (no data loss) and the calibration routing gate is the
 *    user-facing surface for the refusal.
 *  - error: any other status (`5xx`, `404`, `422`, network `0`, …) + a message → retried later.
 */
export type ReplayResult =
  | {
      status: 200 | 201;
      data: { id: string; version: number; state: string; updated_at?: string };
    }
  | { status: 409 | 410; server_state: ServerState }
  | { status: number; error: string; non_retryable: true; code: string }
  | { status: number; error: string };

/**
 * Story 8.1 — the ONE definition of "terminal gate refusal", consulted by the
 * drain transport (drain-runner's `mainThreadReplay`) so no transport-local
 * copy of the retry policy can drift from it.
 * Returns the non-retryable ReplayResult for a recognized terminal code, or
 * null when the error should stay retryable. Scoped to exactly 403 CALIB-001
 * (calibration gate, 8.1) + 403 BILL-001 (subscription gate, Story 13.6 —
 * one code for BOTH org and individual lapse; `details.reason` discriminates
 * upstream, terminality here keys on the code alone) — every other 403
 * (e.g. LEGACY-001) stays retryable on the chance it is transient.
 */
export function terminalReplayError(
  status: number,
  code: string | null | undefined,
  message: string | null | undefined,
): ReplayResult | null {
  if (status === 403 && code === "CALIB-001") {
    return {
      status,
      error: message ?? "calibration_incomplete",
      non_retryable: true,
      code,
    };
  }
  if (status === 403 && code === "BILL-001") {
    return {
      status,
      // Source-neutral fallback: one code covers org AND individual lapse, so a
      // message-less envelope must never assume the B2C reason.
      error: message ?? "entitlement_lapsed",
      non_retryable: true,
      code,
    };
  }
  return null;
}

// --- Injected dependencies (structural) --------------------------------------------------------

/** The two Dexie tables drain-core touches, typed structurally (a minimal surface — orderBy /
 *  get / put / update / delete) so any Dexie `Table` handle satisfies them without a cast. */
export interface DrainDb {
  outbound_queue: {
    orderBy(index: string): { toArray(): Promise<OutboundOp[]> };
    put(op: OutboundOp): Promise<string>;
    // `update` (not a blind `put`) for the retain paths: a no-op if the row was concurrently
    // dequeued, so a stale in-memory snapshot can never resurrect a deleted op.
    update(key: string, changes: Partial<OutboundOp>): Promise<number>;
    delete(key: string): Promise<void>;
  };
  drafts: {
    get(key: string): Promise<DraftRecord | undefined>;
    put(draft: DraftRecord): Promise<string>;
  };
}

export interface DrainDeps {
  /** The shared `franklin` IndexedDB handle (the main-thread `db` singleton). */
  db: DrainDb;
  /** Routes a `create`/`update` op to the network and normalizes the result. Owned by the wiring. */
  replay: (op: OutboundOp) => Promise<ReplayResult>;
  /** R6 test-harness flag (AC7), read from the shared `meta` table. */
  getForceOffline: () => Promise<boolean>;
  /** ISO-8601 clock, injected so `conflict.detected_at` is deterministic under test. */
  now: () => string;
  /**
   * Run `fn` (the local dequeue + draft reconciliation for ONE op) atomically over
   * `outbound_queue` + `drafts`. The wiring supplies a Dexie `rw` transaction so an interruption
   * (e.g. the tab killed mid-drain) can never leave the op deleted but the draft
   * un-reconciled. The network replay runs OUTSIDE this (a Dexie tx can't span a fetch). Optional:
   * when omitted (a minimal test harness) the steps run un-batched — same end-state, no atomicity.
   */
  runAtomic?: (fn: () => Promise<void>) => Promise<void>;
  /**
   * Story 19.6 Unit E — resolve the current device owner (`AuthUser.userId`). The drain then replays
   * ONLY that owner's ops. This is IN SCOPE for the isolation invariant even though no AC names it:
   * on a shared device, a queue still holding clinician A's ops would be replayed by clinician B's
   * session under B's token — pushing A's PHI to the server as B's work, which is worse than merely
   * displaying it. An op the current owner does not own is skipped entirely: not replayed, not
   * dequeued, not collapsed away, not counted.
   *
   * Resolved OUTSIDE `runAtomic` (it reads the `meta` table, which is not in the drain transaction).
   *
   * REQUIRED, unlike `runAtomic` (19.6 review). It was optional, and an absent resolver skipped owner
   * filtering ENTIRELY (`mine = all`) — so deleting the one wiring line in `drain-runner.buildDeps`
   * silently reinstated cross-clinician replay with not one test red. There is no legitimate caller
   * that wants an unfiltered drain, so a missing resolver is now a `tsc -b` error instead of a quiet
   * security regression. A harness that wants "no identity" passes `async () => undefined`, which
   * fails CLOSED (nothing is replayed) rather than open.
   */
  owner: () => Promise<string | undefined>;
}

/** Per-drain tallies. Each attempted op lands in exactly one of succeeded / conflicts / adopted /
 *  failed, so they sum to `queued`. */
export interface DrainStats {
  queued: number; // ops attempted (after collapse: a pending create, then the latest update per eval)
  succeeded: number; // 200/201 success + phantom-resolved 409
  conflicts: number; // real 409 pins (handed to 7.5)
  adopted: number; // 410 silent adoptions
  failed: number; // retained for retry (network / 5xx / 404 / 422 / …)
}

// --- Public entry -----------------------------------------------------------------------------

/**
 * Drain `outbound_queue` once: FIFO by `queued_at`, collapsed to the latest op per `eval_id`,
 * each replayed through the injected transport and reconciled against `drafts` per the AC
 * transition table. Pure: no network or DB handle of its own — everything arrives via `deps`.
 */
export async function drainQueue(deps: DrainDeps): Promise<DrainStats> {
  const stats: DrainStats = {
    queued: 0,
    succeeded: 0,
    conflicts: 0,
    adopted: 0,
    failed: 0,
  };

  // AC7 — force-offline short-circuit: zeroed stats, NO network attempt at all.
  if (await deps.getForceOffline()) return stats;

  // AC6 — FIFO read + per-eval_id collapse. A pending `create` is NEVER collapsed away: for an eval
  // created-then-edited offline the queue is [create, update…], and the chain is [create, latest
  // update] processed in order. Collapsing across op types (the naive "keep only the latest op")
  // would delete the create, and the surviving update would 404 forever against a never-created eval.
  const all = await deps.db.outbound_queue.orderBy("queued_at").toArray();
  // Story 19.6 Unit E — scope the pass to the CURRENT owner's ops before anything else happens, so
  // neither the replay nor the collapse-loser DELETE below can reach another clinician's queued work
  // (or an UNATTRIBUTED pre-v3 op, which belongs to nobody). Those ops stay queued, untouched, for
  // whoever owns them. `deps.owner` is REQUIRED — there is no unfiltered path to fall into (19.6
  // review; see `DrainDeps.owner`).
  const owner = await deps.owner();
  if (!owner) {
    // No resolvable device identity ⇒ we do not know whose ops these are, so replay NONE of them.
    // Fail closed, like the force-offline and no-token gates: replaying under an unknown identity
    // is how one clinician's PHI would reach the server as another's. The ops stay queued for the
    // next pass (the identity is stamped at every login and on the offline restore, so this is
    // practically unreachable) — but a pass that silently replays nothing is indistinguishable
    // from an empty queue, so say so rather than leaving a stuck queue undiagnosable.
    console.warn("[drain] no device owner resolved — skipping the pass; ops stay queued");
    return stats;
  }
  const mine = filterOwned(all, owner);
  const { chains, losers } = collapseByEval(mine);

  // Superseded ops (the older updates the latest update replaces) are obsolete — drop them up front.
  for (const loser of losers) {
    await deps.db.outbound_queue.delete(loser.id);
  }

  // Each eval's chain runs in order; if the create does not reach the server (retryable error),
  // STOP the chain — the dependent update would only 404 until the create lands on a later drain.
  for (const chain of chains) {
    for (const op of chain) {
      stats.queued += 1;
      const advanced = await processOp(deps, op, stats);
      if (!advanced) break;
    }
  }
  return stats;
}

// --- Collapse ---------------------------------------------------------------------------------

/** Group FIFO-ordered ops by `eval_id` into a per-eval CHAIN: at most one `create` (there is only
 *  ever one) followed by the LATEST `update`. Earlier updates are losers (dropped). Evals keep
 *  first-seen order; the create is sequenced before the update so a created-then-edited eval syncs
 *  in a single drain instead of the create being discarded. */
function collapseByEval(ops: OutboundOp[]): {
  chains: OutboundOp[][];
  losers: OutboundOp[];
} {
  const createOp = new Map<string, OutboundOp>();
  const latestUpdate = new Map<string, OutboundOp>();
  const order: string[] = [];
  const seen = new Set<string>();
  for (const op of ops) {
    if (!seen.has(op.eval_id)) {
      seen.add(op.eval_id);
      order.push(op.eval_id);
    }
    // First-create-wins: a producer mints exactly ONE create per eval_id, so this guard only makes
    // the invariant explicit. A stray second create for the same eval_id (a producer bug) is ignored
    // here and dropped as a loser below — never resurrected, never overwriting the original.
    if (op.op === "create") {
      if (!createOp.has(op.eval_id)) createOp.set(op.eval_id, op);
    } else latestUpdate.set(op.eval_id, op); // FIFO → the last update set for an eval_id wins
  }
  const chains: OutboundOp[][] = [];
  const kept = new Set<string>();
  for (const evalId of order) {
    const chain: OutboundOp[] = [];
    const c = createOp.get(evalId);
    const u = latestUpdate.get(evalId);
    if (c) {
      chain.push(c);
      kept.add(c.id);
    }
    if (u) {
      chain.push(u);
      kept.add(u.id);
    }
    chains.push(chain);
  }
  const losers = ops.filter((o) => !kept.has(o.id));
  return { chains, losers };
}

// --- Per-op outcome handling (the AC transition table) ----------------------------------------

/** Replay one op and reconcile per the AC transition table. Returns `true` when the eval advanced
 *  far enough for a dependent op in the same chain to proceed (forward success / phantom-resolved),
 *  `false` when the chain should stop this drain (conflict pinned / adopted / retained for retry). */
async function processOp(
  deps: DrainDeps,
  op: OutboundOp,
  stats: DrainStats,
): Promise<boolean> {
  let result: ReplayResult;
  try {
    result = await deps.replay(op);
  } catch (err) {
    // AC14 — a thrown transport error (network throw) is retryable: retain + record, draft untouched.
    await bumpAttempt(deps, op, errMessage(err));
    stats.failed += 1;
    return false;
  }

  // Narrow on the discriminating property (not `status` alone — the error member's `status: number`
  // overlaps 200/201, so it cannot discriminate the union).
  if ("data" in result) {
    // AC10 — success (200 save / 200 idempotent-create-replay / 201 first create). For a fresh
    // create `data.version` is 0 — correct and expected (AC9).
    const version = result.data.version;
    await atomic(deps, async () => {
      await deps.db.outbound_queue.delete(op.id);
      await mutateDraft(deps, op.eval_id, (d) => {
        d.version = version;
        d.last_synced_version = version;
        d.conflict = null;
      });
    });
    stats.succeeded += 1;
    return true;
  }

  if ("server_state" in result) {
    const ss = result.server_state;

    // AC13 — 410 `EVAL-009 already_submitted`: silent adoption. Keyed on HTTP status 410 AND
    // op.op === "update": only the save-draft (update) path can legitimately 410 (a submitted eval).
    // A 410 on a `create` is unexpected (no backend path emits it) — retain defensively rather than
    // stamping an arbitrary "submitted" state onto a never-created draft. No backend path returns
    // 410 until Story 6.6 lands draft→submitted; implemented defensively + unit-tested, no rework.
    if (result.status === 410) {
      if (op.op !== "update") {
        await bumpAttempt(deps, op, "unexpected 410 on a create op");
        stats.failed += 1;
        return false;
      }
      await atomic(deps, async () => {
        await deps.db.outbound_queue.delete(op.id);
        await mutateDraft(deps, op.eval_id, (d) => {
          d.data = ss.draft_data;
          d.state = "submitted";
          d.version = ss.version;
          d.last_synced_version = ss.version; // adopted == synced to the server's submitted version
          d.conflict = null;
        });
      });
      stats.adopted += 1;
      return false;
    }

    // AC12 — phantom-conflict guard: the queued payload already equals the committed server draft
    // (the previous drain was interrupted AFTER the server committed). Compare JSON-NORMALIZED
    // values — the payload is live JS, but the server draft has been through Postgres JSONB (which
    // drops `undefined`-valued keys and stringifies Dates), so a round-trip-only difference must not
    // read as a conflict. Equal → silently resolve: delete the op, advance version, raise NO conflict.
    if (deepEqual(jsonNormalize(op.payload), ss.draft_data)) {
      await atomic(deps, async () => {
        await deps.db.outbound_queue.delete(op.id);
        await mutateDraft(deps, op.eval_id, (d) => {
          d.version = ss.version;
          d.last_synced_version = ss.version;
          d.conflict = null;
        });
      });
      stats.succeeded += 1;
      return true;
    }

    // AC11 — a real version conflict (409): PIN, never auto-resolve. Retain the op (bump attempts,
    // do not silently re-replay), pin `drafts.conflict` for the 7.5 resolver, advance `version` to
    // the server's. Stop this eval's chain (after collapse there is at most one update per eval).
    const conflict: ConflictBlock = {
      server_state: ss,
      detected_at: deps.now(),
    };
    await atomic(deps, async () => {
      await bumpAttempt(deps, op); // attempts++ only — last_error is for retryable failures
      await mutateDraft(deps, op.eval_id, (d) => {
        d.conflict = conflict;
        d.version = ss.version;
      });
    });
    stats.conflicts += 1;
    return false;
  }

  // Story 8.1 — a terminal gate refusal (403 CALIB-001): DEQUEUE, never retry.
  // Retrying cannot succeed until the user's calibration state changes, and by
  // then the routing gate has already walked them off the eval surfaces — the
  // op would only burn a failed replay on every drain forever (seam-61 note).
  // The draft row is untouched: the clinical data stays local and re-syncable
  // by a future explicit action; the calibration gate is the visible surface.
  if ("non_retryable" in result && result.non_retryable) {
    console.warn(
      `[drain] terminal ${result.status} ${result.code} (op ${op.op} ${op.eval_id}) — dequeued, not retried`,
    );
    await deps.db.outbound_queue.delete(op.id);
    // A terminally refused CREATE strands its chained ops: the eval will never
    // exist server-side, so a queued update for the same eval_id would replay
    // into 404 EVAL-003 on every future drain — the same forever-loop this
    // branch exists to close, one hop later. Drop the whole eval's chain; the
    // drafts row still holds all the data (nothing clinical is lost).
    if (op.op === "create") {
      const remaining = await deps.db.outbound_queue.orderBy("queued_at").toArray();
      for (const chained of remaining) {
        // Story 19.6 Unit E — the owner guard is belt-and-braces (an eval id is minted once, for one
        // clinician, so a same-eval op with a different owner cannot arise), but this branch DELETES
        // ops and no delete in this module may reach a row the pass does not own.
        if (chained.eval_id === op.eval_id && chained.owner === op.owner) {
          console.warn(
            `[drain] dropping chained op ${chained.op} for terminally refused create (eval ${op.eval_id})`,
          );
          await deps.db.outbound_queue.delete(chained.id);
        }
      }
    }
    stats.failed += 1;
    return false;
  }

  // AC14 — every other outcome (5xx / 404 EVAL-003 / 422 / …): retain + record, draft untouched.
  await bumpAttempt(deps, op, result.error);
  stats.failed += 1;
  return false;
}

// --- Small helpers ----------------------------------------------------------------------------

/** Retain an op for the next drain: bump `attempts`, and set `last_error` when a message is given
 *  (a conflict pin bumps attempts but leaves `last_error` untouched). Uses `update` (not a blind
 *  `put` of the in-memory snapshot) so a row concurrently dequeued by another drain is not
 *  resurrected — `update` is a no-op when the key is gone. */
async function bumpAttempt(
  deps: DrainDeps,
  op: OutboundOp,
  lastError?: string,
): Promise<void> {
  const changes: Partial<OutboundOp> = { attempts: op.attempts + 1 };
  if (lastError !== undefined) changes.last_error = lastError;
  await deps.db.outbound_queue.update(op.id, changes);
}

/** Run the local dequeue + draft reconciliation atomically when the wiring provides a transaction
 *  runner (a Dexie `rw` transaction); otherwise run the steps directly (same end-state, no
 *  atomicity guarantee — used by minimal test harnesses). */
async function atomic(deps: DrainDeps, fn: () => Promise<void>): Promise<void> {
  if (deps.runAtomic) return deps.runAtomic(fn);
  return fn();
}

/** Normalize a live JS payload the way Postgres JSONB already normalized the stored server draft
 *  (drop `undefined`-valued keys, Date → ISO string), so the phantom guard compares like with like
 *  and a round-trip-only difference is never a false conflict. */
function jsonNormalize(v: unknown): unknown {
  return JSON.parse(JSON.stringify(v ?? null));
}

/** Read-modify-write a draft row by id. No-op if the row is absent (the producer owns draft
 *  creation; drain-core only reconciles an existing draft). */
async function mutateDraft(
  deps: DrainDeps,
  evalId: string,
  fn: (draft: DraftRecord) => void,
): Promise<void> {
  const draft = await deps.db.drafts.get(evalId);
  if (!draft) return;
  fn(draft);
  await deps.db.drafts.put(draft);
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Order-insensitive (for object keys) deep value-equality used by the phantom guard. Arrays are
 *  compared positionally (ADL score arrays are positional); primitives by `===`. */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (typeof a === "object") {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const ak = Object.keys(ao);
    const bk = Object.keys(bo);
    if (ak.length !== bk.length) return false;
    return ak.every(
      (k) => Object.prototype.hasOwnProperty.call(bo, k) && deepEqual(ao[k], bo[k]),
    );
  }
  return false;
}
